import React, { useEffect, useState } from 'react';
import { API_BASE_URL } from '../config/api';

// 🛫 Expedition queue strip (CHIBI_EXPEDITION_QUEUE_HANDOFF §5.6, 08-12).
// Nina's words: "a waiting list of who is going up next and where they're
// going and where to replay other users' past expeditions."
//
// 🏠 HOME: AgentImportPage.js, gated on `source === 'passport'`. SETTLED
// 08-15 (Nina: "moved to a disabled chibi passport page so that it reappears
// when we fix that up" · "I think that recent expedition banner works better
// there"). Placement is NO LONGER an open question — it lived on /explore
// until 08-15 and was flipped on/off FOUR times in ~40 hours, because this
// header said relocation was "a one-line change" and the page held a
// one-word boolean. Both are gone. Moving it again needs a fresh quoted ask,
// and `npm test` asserts it has not crept back onto /explore
// (ExploreSpacesPage.expeditionStrip.test.js).
//
// ⭐ Its gate is the PASSPORT DOOR, not a flag of its own: it is dark while
// SHOW_CHIBI_PASSPORT_DOOR is false and returns by itself when that flips.
// Renders NOTHING when both the queue and the replay feed are empty, so it
// has zero footprint until the feature is used. ⚠️ That silence is also why
// a stray re-add went unnoticed for days — it shows up only once the queue
// fills, long after the diff that caused it.
//
// Data: GET /api/expeditionQueue/public (jsg dispatcher, no auth) — pending
// queue (public agents only), published replays, and the live session if
// one is out. 60s poll; failures keep the last good payload.
const POLL_MS = 60_000;

const ExpeditionQueueStrip = () => {
  const [data, setData] = useState(null);

  useEffect(() => {
    let gone = false;
    let timer = null;
    const load = () => {
      fetch(`${API_BASE_URL}/api/expeditionQueue/public`)
        .then((r) => r.json())
        .then((d) => { if (!gone && d && d.success) setData(d); })
        .catch(() => { /* keep last good payload */ })
        .finally(() => { if (!gone) timer = setTimeout(load, POLL_MS); });
    };
    load();
    return () => { gone = true; if (timer) clearTimeout(timer); };
  }, []);

  const queue = (data && Array.isArray(data.queue)) ? data.queue : [];
  const feed = (data && Array.isArray(data.feed)) ? data.feed : [];
  if (!queue.length && !feed.length) return null;

  return (
    <div style={S.wrap}>
      {queue.length > 0 && (
        <div style={S.row}>
          <span style={S.rowLabel}>🛫 Next up</span>
          {queue.slice(0, 4).map((q, i) => (
            <span key={q.id || i} style={S.chip} title={q.note || undefined}>
              {q.agent_avatar_url
                ? <img src={q.agent_avatar_url} alt="" style={S.chipAvatar} />
                : <span aria-hidden="true">🛂</span>}
              <span style={S.chipName}>{q.agent_name || 'A chibi'}</span>
              <span style={S.chipDest}>
                → {q.destination_label || 'wandering Spaces'}
              </span>
              {q.maiden && <span title="First walk" aria-label="First walk">🐣</span>}
            </span>
          ))}
          {queue.length > 4 && <span style={S.moreNote}>+{queue.length - 4} more in line</span>}
        </div>
      )}
      {feed.length > 0 && (
        <div style={S.row}>
          <span style={S.rowLabel}>📼 Recent expeditions</span>
          {feed.slice(0, 6).map((f, i) => (
            // ⚠️ Opens in a NEW TAB, deliberately. This strip's only mount is
            // the passport wizard's context step, directly under a textarea
            // holding a passport the user just spent minutes getting their
            // Claude to write. An in-place navigate() threw that away with no
            // confirm and no draft — unnoticed while the funnel was dark, and
            // a first-run data-loss bug the moment it opened.
            <a
              key={f.session || i}
              href={f.session ? `/explorer/theater?replay=${encodeURIComponent(f.session)}` : undefined}
              target="_blank"
              rel="noopener noreferrer"
              style={{ ...S.chip, ...S.chipButton, textDecoration: 'none' }}
              title="Watch the replay in a new tab"
            >
              <span aria-hidden="true">▶</span>
              <span style={S.chipName}>{f.agent_name || 'A chibi'}</span>
              {f.started_at && (
                <span style={S.chipDest}>
                  {new Date(f.started_at).toLocaleDateString([], { month: 'short', day: 'numeric' })}
                </span>
              )}
            </a>
          ))}
        </div>
      )}
    </div>
  );
};

// /explore is a LIGHT page (liveRailTitle is #1a1a1f) — the palette here is
// light with the explorer's purple accent (review confirm 08-12: the first
// cut assumed a dark page and was unreadable in place).
const S = {
  wrap: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    margin: '0 0 26px',
    padding: '10px 14px',
    borderRadius: 12,
    background: '#f6f1fd',
    border: '1px solid #e3d7f5',
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    flexWrap: 'wrap',
    minWidth: 0,
  },
  rowLabel: {
    fontSize: 12,
    fontWeight: 800,
    letterSpacing: '0.04em',
    color: '#7b1fa2',
    textTransform: 'uppercase',
    flexShrink: 0,
  },
  chip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '4px 10px',
    borderRadius: 999,
    background: '#fff',
    border: '1px solid #ddd0f0',
    color: '#1a1a1f',
    fontSize: 12.5,
    maxWidth: '100%',
    minWidth: 0,
  },
  chipButton: {
    cursor: 'pointer',
    font: 'inherit',
    fontSize: 12.5,
  },
  chipAvatar: {
    width: 18,
    height: 18,
    borderRadius: '50%',
    objectFit: 'cover',
    flexShrink: 0,
  },
  chipName: {
    fontWeight: 700,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: 140,
  },
  chipDest: {
    color: '#5a5a66',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: 200,
  },
  moreNote: {
    fontSize: 12,
    color: '#5a5a66',
  },
};

export default ExpeditionQueueStrip;
